import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = "https://backend.dlapakistan.com/api/dla-trade/headline-sponsor";

const DLAHeadlineSponsor = () => {
  const [sponsorData, setSponsorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState('/DLA Headline Sponsor1.jpg'); // Default fallback image

  useEffect(() => {
    fetchSponsorData();
  }, []);

  const fetchSponsorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(API_BASE_URL + "/");
      
      if (response.data) {
        let data = response.data;
        
        // Handle both array and single object responses
        if (Array.isArray(data) && data.length > 0) {
          data = data[0];
        }
        
        setSponsorData(data);
        
        // Set image URL from backend if available
        if (data.image_url) {
          // Add cache busting to prevent browser caching
          const cacheBuster = `?t=${Date.now()}`;
          const fullImageUrl = data.image_url.includes('?') 
            ? data.image_url + '&' + cacheBuster.substring(1)
            : data.image_url + cacheBuster;
          setImageUrl(fullImageUrl);
        }
      }
    } catch (error) {
      console.error("Error fetching sponsor data:", error);
      setError("Failed to load sponsor data. Showing default content.");
      
      // Fallback to localStorage if available
      const savedData = localStorage.getItem("sponsorHeadline_Storage");
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData.length > 0) {
            setSponsorData(parsedData[0]);
            if (parsedData[0].image_url || parsedData[0].image) {
              setImageUrl(parsedData[0].image_url || parsedData[0].image);
            }
          }
        } catch (e) {
          console.error("Error parsing saved data:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // If loading, show skeleton
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 font-sans text-[#3c4858]">
        <div className="animate-pulse">
          {/* Breadcrumb skeleton */}
          <div className="flex items-center gap-1 mb-4">
            <div className="h-4 w-16 bg-gray-200 rounded"></div>
            <div className="h-4 w-4 bg-gray-200 rounded"></div>
            <div className="h-4 w-20 bg-gray-200 rounded"></div>
            <div className="h-4 w-4 bg-gray-200 rounded"></div>
            <div className="h-4 w-32 bg-gray-200 rounded"></div>
          </div>
          
          {/* Title skeleton */}
          <div className="h-12 w-64 bg-gray-200 rounded mb-4"></div>
          
          {/* Line */}
          <div className="h-px bg-gray-200 mb-8"></div>
          
          {/* Content skeleton - Image and subtitle side by side */}
          <div className="flex flex-col md:flex-row gap-8 mb-8">
            <div className="md:w-[400px] h-[300px] bg-gray-200 rounded"></div>
            <div className="flex-1">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
          
          {/* Description skeleton */}
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 font-sans text-[#3c4858]">
      {/* Error message if any */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded">
          <p className="text-sm">{error}</p>
        </div>
      )}



      {/* Main Title - Use data from backend or default */}
      <h1 className="text-[42px] font-normal mb-4 text-[#3c4858]">
        {sponsorData?.title || "DLA Headline Sponsor"}
      </h1>
      
      {/* Horizontal Line */}
      <hr className="border-t border-gray-200 mb-8" />

      {/* IMAGE AND SUBTITLE SIDE BY SIDE LAYOUT */}
      <div className="flex flex-col md:flex-row gap-10 mb-12">
        
        {/* Logo Image on Left */}
        <div className="w-full md:w-[400px] flex-shrink-0">
          <img 
            src={imageUrl}
            alt={sponsorData?.title || "Headline Sponsor"} 
            className="w-full h-auto rounded-lg shadow-md"
            onError={(e) => {
              e.target.src = '/DLA Headline Sponsor1.jpg'; // Fallback to default
              e.target.alt = "Default sponsor image";
            }}
          />
        </div>
        
        {/* Subtitle on Right Side of Image */}
        <div className="flex-1">
          {sponsorData?.subtitle ? (
            <div className="text-[20px] leading-[1.6] text-[#555555] italic border-l-4 border-[#70B444] pl-4 py-2">
              {sponsorData.subtitle}
            </div>
          ) : (
            <div className="text-[17px] leading-[1.8] text-[#555555]">
              <p>
                The Dental Laboratories Association have had a long history of support from 
                the team at Skillbond, so we are delighted that for a second year they 
                have agreed to be our headline sponsors for the year.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* DESCRIPTION BELOW THE IMAGE */}
      <div className="w-full">
        <div className="text-[17px] leading-[1.8] text-[#555555]">
          {sponsorData?.description ? (
            <div 
              className="description-content"
              dangerouslySetInnerHTML={{ __html: sponsorData.description }}
            />
          ) : (
            <>
              <p className="mb-6">
                Skillbond stock and supply over 35,000 products exclusively for the dental 
                laboratory technician. Skillbond's range includes porcelains, diestones, investments, 
                diamonds, burs, teeth, laboratory equipment and furniture from the world's leading 
                manufacturers. Skillbond provide a next day delivery service and offer highly 
                competitive prices.
              </p>

              <p className="mb-6">
                Skillbond is a subsidiary of the Argen Corporation and lead the Pakistan market in the 
                supply of precious and non-precious dental alloys. Argen are world leaders in the 
                research and manufacture of dental alloys and are based in San Diego.
              </p>

              <p className="mb-6">
                Skillbond's outstanding reputation for service is achieved by their highly motivated 
                team of dental professionals, service engineers, sales and support staff, whose aim 
                is to deliver 100% customer satisfaction. Established in 1972, Skillbond are based in 
                High Wycombe in Buckinghamshire.
              </p>
            </>
          )}

          {/* Optional link - Only show if we have data from backend */}
          {sponsorData && (
            <p className="mt-8">
              To find out more information on our sponsor you can visit their website by{' '}
              <a 
                href="#"
                className="text-[#70B444] hover:underline cursor-pointer font-bold"
                onClick={(e) => {
                  e.preventDefault();
                  // Add your website URL logic here
                  alert('Sponsor website link would open here');
                }}
              >
                clicking here
              </a>
            </p>
          )}
        </div>
      </div>

      <style jsx>{`
        .description-content p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
          color: #555555;
          font-size: 17px;
        }
        
        .description-content h1 {
          font-size: 32px;
          color: #3c4858;
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: normal;
        }
        
        .description-content h2 {
          font-size: 28px;
          color: #3c4858;
          margin-top: 1.8rem;
          margin-bottom: 1rem;
          font-weight: normal;
        }
        
        .description-content h3 {
          font-size: 24px;
          color: #3c4858;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          font-weight: normal;
        }
        
        .description-content h4,
        .description-content h5,
        .description-content h6 {
          color: #3c4858;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .description-content ul,
        .description-content ol {
          margin-bottom: 1.5rem;
          padding-left: 2rem;
          color: #555555;
        }
        
        .description-content li {
          margin-bottom: 0.5rem;
          line-height: 1.8;
        }
        
        .description-content a {
          color: #70B444;
          text-decoration: underline;
        }
        
        .description-content a:hover {
          text-decoration: none;
        }
        
        .description-content img {
          max-width: 100%;
          height: auto;
          margin: 1.5rem 0;
          border-radius: 8px;
        }
        
        .description-content blockquote {
          border-left: 4px solid #70B444;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #666;
        }
        
        .description-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        
        .description-content table th,
        .description-content table td {
          padding: 0.75rem;
          border: 1px solid #ddd;
        }
        
        .description-content table th {
          background-color: #f8f9fa;
          font-weight: bold;
          color: #3c4858;
        }
      `}</style>
    </div>
  );
};

export default DLAHeadlineSponsor;